import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  deleteUser,
  fetchUsers,
  reactivateUser,
  suspendUser,
} from "services/complaintDetailService";
import { User } from "types";
import { errorHandler } from "utils/helper";
import PageHeader from "../../components/PageHeader";
import ConfirmModal from "components/ConfirmModal";

const tabs = ["All"];

const UsersList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<
    "suspend" | "reactivate" | "delete" | null
  >(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetchData = () => {
    setLoading(true);
    fetchUsers()
      .then((data) => {
        setUsers(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    const id = u?.id?.toString()?.toLowerCase() || "";
    const name = u?.full_name?.toLowerCase() || "";

    const matchesText = id.includes(searchTerm) || name.includes(searchTerm);

    return matchesText;
  });

  // Handlers for modal actions
  const handleSuspend = (user: User) => {
    setSelectedUser(user);
    setModalType("suspend");
    setModalOpen(true);
  };

  const handleReactivate = (user: User) => {
    setSelectedUser(user);
    setModalType("reactivate");
    setModalOpen(true);
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setModalType("delete");
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      if (modalType === "suspend") {
        await suspendUser(selectedUser.id);
        showTooltip("User suspended!", "success");
      } else if (modalType === "reactivate") {
        await reactivateUser(selectedUser.id);
        showTooltip("User reactivated!", "success");
      } else if (modalType === "delete") {
        await deleteUser(selectedUser.id);
        showTooltip("User deleted!", "success");
      }
      fetchData();
    } catch (error: any) {
      showTooltip(errorHandler(error), "error");
    } finally {
      setModalOpen(false);
      setSelectedUser(null);
      setModalType(null);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setModalType(null);
  };

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title="Users"
        icon={FaBriefcase}
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label="Loading Users" />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title="Go Back"
                        >
                          ◀️
                        </button>
                        <span>User Table</span>
                      </div>
                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search"
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">Full Name</th>
                  <th className="p-3 text-left">email</th>
                  <th className="p-3 text-left">Is Active</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((data, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                    >
                      <td className="p-3 font-[500]">{data?.full_name}</td>
                      <td className="p-3 font-[500]">{data?.email}</td>
                      <td className="p-3 font-[500]">
                        {data?.is_active ? (
                          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700 border border-green-300">
                            Active
                          </span>
                        ) : (
                          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 border border-red-300">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="p-3 font-[500]">
                        <div className="flex flex-row items-center justify-end gap-4">
                          {data?.is_active ? (
                            <button
                              className="bg-black text-white px-4 py-0.5 rounded hover:underline"
                              onClick={() => handleSuspend(data)}
                            >
                              Suspend
                            </button>
                          ) : (
                            <button
                              className="bg-green-600 text-white px-4 py-0.5 rounded hover:underline"
                              onClick={() => handleReactivate(data)}
                            >
                              Reactivate
                            </button>
                          )}
                          <button
                            className="bg-red-500 text-white px-4 py-0.5 rounded hover:underline"
                            onClick={() => handleDelete(data)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-gray-500">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <ConfirmModal
        open={modalOpen}
        title={
          modalType === "suspend"
            ? "Suspend User"
            : modalType === "delete"
            ? "Delete User"
            : ""
        }
        message={
          modalType === "suspend"
            ? `Are you sure you want to suspend ${
                selectedUser?.full_name || "this user"
              }?`
            : modalType === "delete"
            ? `Are you sure you want to delete ${
                selectedUser?.full_name || "this user"
              }?`
            : ""
        }
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default UsersList;
